import {
  LTLAccessorial,
  LTLItem,
  LTLSelectedAccessorial,
  LTLUnitSystem,
  LTL_UNIT_SYSTEMS,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';

export const TAI_OVERLENGTH_ACCESSORIAL_CODES = [
  'OVR8TO12',
  'OVR12TO16',
  'OVR16TO20',
  'OVR20',
] as const;

export type TaiOverlengthAccessorialCode =
  (typeof TAI_OVERLENGTH_ACCESSORIAL_CODES)[number];

const OVERLENGTH_CODE_BY_MINIMUM_INCHES: ReadonlyArray<{
  minimumInches: number;
  code: TaiOverlengthAccessorialCode;
}> = [
  { minimumInches: 240, code: 'OVR20' },
  { minimumInches: 192, code: 'OVR16TO20' },
  { minimumInches: 144, code: 'OVR12TO16' },
  { minimumInches: 96, code: 'OVR8TO12' },
];

const OVERLENGTH_CODE_SET = new Set<string>(TAI_OVERLENGTH_ACCESSORIAL_CODES);

function normalizeCode(code?: string): string {
  return code?.trim().toUpperCase() ?? '';
}

function toPositiveNumber(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function toInches(value: number, unitSystem: LTLUnitSystem): number {
  return unitSystem === LTL_UNIT_SYSTEMS.METRIC ? value / 2.54 : value;
}

/** Returns the Tai overlength code implied by the largest dimension in the shipment. */
export function getTaiOverlengthAccessorialCode(
  items: LTLItem[],
  unitSystem: LTLUnitSystem
): TaiOverlengthAccessorialCode | undefined {
  let largestDimensionInches = 0;

  items.forEach((item) => {
    [item.length, item.width, item.height].forEach((rawDimension) => {
      const dimension = toPositiveNumber(rawDimension);
      if (dimension == null) {
        return;
      }

      largestDimensionInches = Math.max(
        largestDimensionInches,
        toInches(dimension, unitSystem)
      );
    });
  });

  return OVERLENGTH_CODE_BY_MINIMUM_INCHES.find(
    ({ minimumInches }) => largestDimensionInches >= minimumInches
  )?.code;
}

function getSelectionCode(
  selected: LTLSelectedAccessorial,
  accessorials: LTLAccessorial[]
): string {
  const selectedCode = normalizeCode(selected.code);
  if (selectedCode) {
    return selectedCode;
  }

  if (selected.externalId == null) {
    return '';
  }

  return normalizeCode(
    accessorials.find(
      (accessorial) => accessorial.externalId === selected.externalId
    )?.code
  );
}

function isOverlengthSelection(
  selected: LTLSelectedAccessorial,
  accessorials: LTLAccessorial[]
): boolean {
  return OVERLENGTH_CODE_SET.has(getSelectionCode(selected, accessorials));
}

function selectionMatchesDefinition(
  selected: LTLSelectedAccessorial,
  definition: LTLAccessorial
): boolean {
  return (
    normalizeCode(selected.code) === normalizeCode(definition.code) &&
    selected.externalId === definition.externalId &&
    selected.name === definition.name
  );
}

/**
 * Keeps non-overlength selections intact while enforcing the single OVR code
 * implied by the current dimensions. Returns the original array when no form
 * update is required.
 */
export function reconcileTaiOverlengthAccessorials(
  selectedAccessorials: LTLSelectedAccessorial[],
  availableAccessorials: LTLAccessorial[],
  requiredCode?: TaiOverlengthAccessorialCode
): LTLSelectedAccessorial[] {
  const selectedOverlength = selectedAccessorials.filter((selected) =>
    isOverlengthSelection(selected, availableAccessorials)
  );

  if (!requiredCode) {
    if (selectedOverlength.length === 0) {
      return selectedAccessorials;
    }

    return selectedAccessorials.filter(
      (selected) => !isOverlengthSelection(selected, availableAccessorials)
    );
  }

  const requiredDefinition = availableAccessorials.find(
    (accessorial) => normalizeCode(accessorial.code) === requiredCode
  );
  if (!requiredDefinition) {
    return selectedAccessorials;
  }

  if (
    selectedOverlength.length === 1 &&
    selectionMatchesDefinition(selectedOverlength[0], requiredDefinition)
  ) {
    return selectedAccessorials;
  }

  return [
    ...selectedAccessorials.filter(
      (selected) => !isOverlengthSelection(selected, availableAccessorials)
    ),
    {
      code: requiredDefinition.code,
      name: requiredDefinition.name,
      externalId: requiredDefinition.externalId,
    },
  ];
}
